/**
 * Builds the "Full Address" text from whichever address parts are filled in.
 * Postcode lookups often have no house number or county, so a missing part
 * must not leave the (required) Full Address empty.
 */
export const joinAddressParts = (
  parts: Array<string | null | undefined>,
): string =>
  parts
    .map((part) => String(part ?? '').trim())
    .filter(Boolean)
    .join(', ');

/** Capitalises each word the same way the First Name / Surname inputs do. */
export const capitalizeWords = (value: string): string =>
  value
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

/**
 * Splits a name typed elsewhere (e.g. the appointment patient search) into
 * first name and surname: the first word is the first name, the rest is the
 * surname. A single word becomes the first name only.
 */
export const splitInitialName = (
  name: string | null | undefined,
): { firstName: string; surName: string } => {
  const trimmed = String(name ?? '')
    .trim()
    .replace(/\s+/g, ' ');
  if (!trimmed) return { firstName: '', surName: '' };
  const spaceAt = trimmed.indexOf(' ');
  if (spaceAt === -1) {
    return { firstName: capitalizeWords(trimmed), surName: '' };
  }
  return {
    firstName: capitalizeWords(trimmed.slice(0, spaceAt)),
    surName: capitalizeWords(trimmed.slice(spaceAt + 1)),
  };
};
